import { useMemo } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuditLogs } from '@/features/audit-logs/api';
import {
  mapAuditLogsToTrailEntries,
  type AuditTrailChange,
  type AuditTrailEntry,
} from '@/features/audit-logs/utils/auditTrail';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { VBMCardTitle, VBMCardWrapper } from '..';

type VBMSessionActivityProps = {
  bookingSessionId: number;
  maxItems?: number;
};

const DEFAULT_MAX_ITEMS = 20;
const MAX_VISIBLE_CHANGES = 6;

const AVATAR_COLORS = [
  '#0ea5e9',
  '#16a34a',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#0891b2',
  '#4f46e5',
  '#64748b',
];

const FIELD_LABELS: Record<string, string> = {
  access_instructions: 'Access instructions',
  admin_notes: 'Office notes',
  booking_cleaners: 'Cleaner',
  booking_session_cleaners: 'Cleaner',
  booking_session_payment_status: 'Payment status',
  booking_session_status: 'Status',
  booking_session_status_id: 'Status',
  cleaner_id: 'Cleaner',
  payment_status_id: 'Payment status',
  provider_id: 'Cleaner',
  provider_instructions: 'Cleaning instructions',
  status_id: 'Status',
  version: 'Version',
};

const getStringSeed = (value: unknown) => String(value ?? '');

const getAvatarColor = (seed: unknown) => {
  const text = getStringSeed(seed);
  const total = text
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);

  return AVATAR_COLORS[total % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return 'DU';

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const getActorName = (entry: AuditTrailEntry) =>
  entry.actorName?.trim() ||
  entry.actorEmail?.trim() ||
  (entry.actorUserId
    ? `Dashboard user #${entry.actorUserId}`
    : 'Dashboard user');

const formatActivityCreatedAt = (entry: AuditTrailEntry) => {
  const date = new Date(entry.createdAt);

  return Number.isNaN(date.getTime())
    ? 'Unknown time'
    : format(date, 'MMM d, p');
};

const getActivityTitle = (entry: AuditTrailEntry) => {
  if (entry.action === 'create') return 'created this session';
  if (entry.action === 'delete') return 'deleted this session';

  return 'updated this session';
};

const titleCase = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeFieldKey = (field: string) =>
  field
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

const getLeafFieldName = (field: string) => {
  const normalizedPath = field.trim();
  const segments = normalizedPath
    .split(/[.[\]]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return segments[segments.length - 1] ?? normalizedPath;
};

const getChangeFieldLabel = (field: string) => {
  const leafFieldName = getLeafFieldName(field);
  const normalized = normalizeFieldKey(leafFieldName);

  return FIELD_LABELS[normalized] ?? titleCase(leafFieldName);
};

const getChangeLabel = (change: AuditTrailChange) =>
  `${getChangeFieldLabel(change.field)} changed`;

const formatChangeValue = (value?: string) => value || 'Empty';

const ChangeValueBlock = ({
  label,
  value,
}: {
  label: string;
  value?: string;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(255,255,255,0.68)',
        display: 'block',
        fontWeight: 700,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: '#ffffff',
        display: 'block',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        lineHeight: 1.45,
      }}
    >
      {formatChangeValue(value)}
    </Typography>
  </Box>
);

const ChangeTooltipContent = ({ change }: { change: AuditTrailChange }) => {
  const fieldLabel = getChangeFieldLabel(change.field);
  const showBefore = change.status !== 'added';
  const showAfter = change.status !== 'removed';

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: '#ffffff',
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1.3,
          mb: 0.75,
        }}
      >
        {fieldLabel}
      </Typography>
      <Stack spacing={0.9}>
        {showBefore && (
          <ChangeValueBlock label="Before" value={change.before} />
        )}
        {showAfter && <ChangeValueBlock label="After" value={change.after} />}
      </Stack>
    </Box>
  );
};

function SessionActivityItem({
  entry,
  isLast,
}: {
  entry: AuditTrailEntry;
  isLast: boolean;
}) {
  const actorName = getActorName(entry);
  const changes = entry.changes ?? [];
  const visibleChanges = changes.slice(0, MAX_VISIBLE_CHANGES);
  const hiddenChangeCount = Math.max(changes.length - visibleChanges.length, 0);

  return (
    <Stack
      data-testid="session-activity-item"
      direction="row"
      spacing={1.25}
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignSelf: 'stretch',
          width: 34,
          flexShrink: 0,
          pt: 0.2,
        }}
      >
        <Avatar
          alt={actorName}
          sx={{
            width: 34,
            height: 34,
            fontSize: 12,
            fontWeight: 800,
            color: '#ffffff',
            bgcolor: getAvatarColor(entry.actorUserId ?? actorName),
            zIndex: 1,
          }}
        >
          {getInitials(actorName)}
        </Avatar>
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              top: 38,
              bottom: 0,
              width: 2,
              bgcolor: '#e2e8f0',
            }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0, flexGrow: 1, pb: isLast ? 0 : 1.75 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.25, sm: 1 }}
          alignItems={{ xs: 'flex-start', sm: 'baseline' }}
        >
          <Typography variant="subtitle2" sx={{ color: '#0f172a' }}>
            {actorName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {formatActivityCreatedAt(entry)}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: '#334155',
            fontWeight: 700,
            mt: 0.15,
            overflowWrap: 'anywhere',
          }}
        >
          {getActivityTitle(entry)}
        </Typography>

        {entry.message && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: '#64748b',
              mt: 0.35,
              overflowWrap: 'anywhere',
            }}
          >
            {entry.message}
          </Typography>
        )}

        {!!visibleChanges.length && (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.75 }}
          >
            {visibleChanges.map((change, index) => (
              <Tooltip
                key={`${change.field}-${index}`}
                title={<ChangeTooltipContent change={change} />}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 560,
                      p: 1.25,
                    },
                  },
                }}
              >
                <Chip
                  label={getChangeLabel(change)}
                  size="small"
                  sx={{
                    height: 'auto',
                    minHeight: 24,
                    borderRadius: 0.85,
                    bgcolor: '#f1f5f9',
                    color: '#475569',
                    fontWeight: 700,
                    '& .MuiChip-label': {
                      py: 0.35,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    },
                  }}
                />
              </Tooltip>
            ))}
            {hiddenChangeCount > 0 && (
              <Tooltip
                title={
                  <Stack spacing={1.25}>
                    {changes.map((change, index) => (
                      <ChangeTooltipContent
                        key={`${change.field}-${index}`}
                        change={change}
                      />
                    ))}
                  </Stack>
                }
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 640,
                      p: 1.25,
                    },
                  },
                }}
              >
                <Chip
                  label={`+${hiddenChangeCount} more`}
                  size="small"
                  sx={{
                    height: 'auto',
                    minHeight: 24,
                    borderRadius: 0.85,
                    bgcolor: '#e2e8f0',
                    color: '#475569',
                    fontWeight: 700,
                    '& .MuiChip-label': {
                      py: 0.35,
                    },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

export default function VBMSessionActivity({
  bookingSessionId,
  maxItems = DEFAULT_MAX_ITEMS,
}: VBMSessionActivityProps) {
  const { canView } = useGetAccess('audit_logs');
  const { errorMsg } = useSnackbarMsg();
  const itemLimit = Math.max(1, maxItems);

  const { data, isError, isLoading } = useQuery({
    queryKey: [
      'audit-logs',
      'booking-session-activity',
      bookingSessionId,
      itemLimit,
    ],
    queryFn: () =>
      getAuditLogs({
        page: 1,
        limit: itemLimit,
        resource_name: 'booking_session',
        resourceName: 'booking_session',
        resource_id: bookingSessionId,
        resourceId: bookingSessionId,
        sortBy: 'created_at',
        sortOrder: 'desc',
      }),
    enabled: canView && Number.isFinite(bookingSessionId),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Unable to load session activity');
    },
  });

  const entries = useMemo(
    () => mapAuditLogsToTrailEntries(data?.data ?? []).slice(0, itemLimit),
    [data?.data, itemLimit]
  );

  if (!canView) {
    return null;
  }

  return (
    <VBMCardWrapper>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.25 }}
      >
        <VBMCardTitle title="Activity" />
        {!isLoading && !isError && (
          <Chip
            size="small"
            variant="outlined"
            label={entries.length}
            sx={{ borderRadius: 999 }}
          />
        )}
      </Stack>

      {isLoading ? (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ py: 2 }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading session activity...
          </Typography>
        </Stack>
      ) : isError ? (
        <Alert severity="error">Unable to load session activity.</Alert>
      ) : entries.length ? (
        <Box sx={{ maxHeight: 280, overflow: 'auto', pr: 0.5 }}>
          <Stack spacing={0}>
            {entries.map((entry, index) => (
              <SessionActivityItem
                key={entry.id}
                entry={entry}
                isLast={index === entries.length - 1}
              />
            ))}
          </Stack>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 1 }}>
          No activity recorded for this session yet.
        </Typography>
      )}
    </VBMCardWrapper>
  );
}
